import { memo, ReactElement, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { ALIGN_TYPE, Element, RouterLink } from 'src/components';
import Card from 'src/components/card';
import { ICluster, IContent, IContentItemClass } from 'src/interface';
import { CONTENT_TYPE_VALUE } from 'src/constants';
import { isDesktopSelector } from 'src/selectors/app';
import { ContentFooter } from '../../shared/ContentFooter';
import { ContentThumb } from '../../shared/ContentThumb';
import { ContentTitle } from '../../shared/ContentTitle';
import { ContentDescription } from '../../shared/ContentDescription';
import { ContentHeader } from '../../shared/ContentHeader';
import { ContentCluster } from '../../shared/ContentCluster';

interface IProps {
    item: IContent;
    lazyloadImage?: boolean;
    displayTimeIn?: number;
    align?: ALIGN_TYPE;
    className?: string;
    title?: string;
    ContentHeaderProps?: ReactElement;
    Footer?: ReactElement;
    ContentItemClass?: IContentItemClass;
    ContentItemProps?: any;
    showDescription?: boolean;
    showCluster?: boolean;
    truncateDescription?: boolean;
    newTab?: boolean;
    domRef?: any;
    onClick: (evt: React.MouseEvent) => void;
}

export function ContentNormalItem(props: IProps) {
    const {
        item,
        align,
        lazyloadImage = true,
        displayTimeIn,
        className,
        title = item.title,
        ContentHeaderProps,
        ContentItemClass = {},
        Footer = (
            <ContentFooter
                content={item}
                displayTimeIn={displayTimeIn}
                {...props}
            />
        ),
        showDescription = false,
        showCluster = false,
        truncateDescription = true,
        domRef,
        onClick,
    } = props;
    const { header, contentTypes = [] } = item;
    const isDesktop = useSelector(isDesktopSelector);

    const getClassList = useMemo(() => {
        let className = '';
        if (align === 'vertical') {
            className = 'flex-col';
        }

        return className;
    }, [align]);

    const _ContentItemClass = useMemo(() => {
        const { CardClass, CardHeaderClass, CardImageClass, CardContentClass } =
            ContentItemClass;
        let classAlign: any = {};
        if (align === 'vertical') {
            classAlign = {
                CardContentClass: 'ml-0',
            };
        } else {
            classAlign = {
                CardImageClass: 'my-[4px]',
            };
        }

        let classType: any = {};
        if (
            contentTypes.includes(CONTENT_TYPE_VALUE.PHOTO) ||
            contentTypes.includes(CONTENT_TYPE_VALUE.VIDEO)
        ) {
            classType = {
                CardImageClass: cn(
                    'before:absolute before:right-[5px] before:bottom-[5px]',
                    'before:w-[23px] before:h-[23px]',
                    'before:rounded-[15px]',
                    'before:inline-block',
                    'before:bg-black/50',
                    "before:bg-[url('https://baomoi-static.bmcdn.me/web/styles/img/bm-icon-2.0.3.png')]",
                    'before:bg-[length:100px_4250px]',
                    'before:z-[2]',
                    contentTypes.includes(CONTENT_TYPE_VALUE.PHOTO)
                        ? 'before:bg-[position:0_-700px]'
                        : 'before:bg-[position:0_-750px]'
                ),
            };
        }

        const _ContentItemClass = Object.assign({}, ContentItemClass, {
            CardClass: cn(classType?.CardClass, CardClass),
            CardHeaderClass: cn(classType?.CardHeaderClass, CardHeaderClass),
            CardImageClass: cn(
                classType?.CardImageClass,
                classAlign?.CardImageClass,
                CardImageClass
            ),
            CardContentClass: cn(
                classType?.CardContentClass,
                CardContentClass,
                classAlign?.CardContentClass
            ),
        });

        return _ContentItemClass;
    }, [item.contentId, align, ContentItemClass]);

    const {
        CardClass,
        CardHeaderClass,
        CardImageClass,
        CardFigureClass,
        CardVideoClass,
        CardContentClass,
        CardFooterClass,
        ClusterClass,
    } = _ContentItemClass;

    const renderContent = useCallback(() => {
        return (
            <>
                <ContentThumb
                    src={item.thumb}
                    thumbIsGif={item.thumbIsGif}
                    title={item.title}
                    lazyload={lazyloadImage}
                    CardImageClass={CardImageClass}
                    CardFigureClass={CardFigureClass}
                    CardVideoClass={CardVideoClass}
                />
                <Card.Content className={CardContentClass}>
                    {ContentHeaderProps ? (
                        ContentHeaderProps
                    ) : (
                        <Card.Header className={CardHeaderClass}>
                            <ContentTitle title={title} />
                        </Card.Header>
                    )}
                    <Card.Footer className={CardFooterClass}>
                        {contentTypes.includes(CONTENT_TYPE_VALUE.LIVE) && (
                            <Element
                                className={cn(
                                    'inline-flex items-center justify-center',
                                    'text-white text-[1.1rem] leading-[13px] font-bold tracking-[1px] uppercase',
                                    'px-[8px] py-[2px]',
                                    'rounded-[9px]',
                                    'bg-[rgba(255,0,0,0.7)]',
                                    'animate-[blink_1s_linear_infinite]'
                                )}
                            >
                                Live
                            </Element>
                        )}
                        {Footer}
                    </Card.Footer>
                    {showDescription && (
                        <ContentDescription
                            description={item.description}
                            hasTruncate={truncateDescription}
                            objEllipsis={{ widthContainer: 212, line: 4 }}
                        />
                    )}
                </Card.Content>
            </>
        );
    }, [item]);

    return (
        <>
            {header && <ContentHeader header={header} />}
            <RouterLink
                className={cn('bm-card', CardClass, className, getClassList)}
                to={isDesktop ? item.redirectUrl : item.url}
                ref={domRef}
                onClick={onClick}
                title={item.title}
            >
                {renderContent()}
            </RouterLink>
            {showCluster && (
                <ContentCluster
                    className={ClusterClass}
                    cluster={item.cluster as ICluster}
                />
            )}
        </>
    );
}

export default memo(ContentNormalItem);
